import _orderBy from 'lodash/orderBy';
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import CandidatePickerItemPresenter from './item/candidatePickerItemPresenter';
import CandidatesPickerPresenter from './candidatesPickerPresenter';
import { getTranslatedValue } from '../../../../../utils/localizedString';
import { CandidateStatus } from '../../../../../firebase/firestore/documents/candidate';
import { useSeekerContainerContext } from '../../SeekerContainerContext';
import { useSelectedEngagedCandidate } from '../../../MessagingViewContext';
import { checkIfUserRestrictedInPositions, filteredCandidatesByRestriction } from '../../../../../services/userService';
import { useStoreState } from '../../../../../models/hooks';

const useCandidatesPicker = (): CandidatesPickerPresenter => {
  const { i18n } = useTranslation();
  const { candidates } = useSeekerContainerContext();
  const selectedEngagedCandidate = useSelectedEngagedCandidate();

  const appOwnersId = useStoreState((s) => s.app.accounts?.owners);
  const appUserId = useStoreState((s) => s.app.user?.id);
  const accounts = useStoreState((s) => s.app.accounts);
  const restrictPositions = useStoreState((s) => s.app.user?.restrictPositions);
  const superOwners = accounts?.superOwners ?? [];

  const isUserRestrictedInPositions = useMemo(() => {
    return checkIfUserRestrictedInPositions({
      appOwnersId,
      appUserId,
      superOwners,
      isUserRestricted: restrictPositions,
    });
  }, [appOwnersId, appUserId, superOwners, restrictPositions]);

  const candidateToDisplay = useMemo(() => {
    const filteredCandidatesAfterUserRestriction = filteredCandidatesByRestriction(
      candidates,
      isUserRestrictedInPositions,
      appUserId,
    );

    const filteredSelectedEngagedCandidate = filteredCandidatesByRestriction(
      selectedEngagedCandidate ? [selectedEngagedCandidate] : [],
      isUserRestrictedInPositions,
      appUserId,
    );

    return filteredCandidatesAfterUserRestriction && filteredCandidatesAfterUserRestriction.length > 0
      ? filteredCandidatesAfterUserRestriction
      : filteredSelectedEngagedCandidate;
  }, [candidates, isUserRestrictedInPositions, appUserId, selectedEngagedCandidate]);

  return useMemo((): CandidatesPickerPresenter => {
    const candidateItems: CandidatePickerItemPresenter[] = [];
    /* istanbul ignore else */
    if (candidateToDisplay) {
      const orderedCandidates = _orderBy(
        candidateToDisplay,
        [(candidate) => candidate?.computedStatus?.lastUpdate || 0],
        ['desc'],
      );
      orderedCandidates?.forEach((candidate) => {
        // istanbul ignore else
        if (candidate) {
          candidateItems.push(
            new CandidatePickerItemPresenter(
              candidate.id,
              candidate.computedStatus?.status || CandidateStatus.Unknown,
              getTranslatedValue(candidate.jobTitle, i18n.language),
              candidate.businessName,
              candidate.businessCompleteAddress || '',
              candidate,
            ),
          );
        }
      });
    }
    return new CandidatesPickerPresenter(candidateItems);
  }, [i18n.language, candidateToDisplay]);
};

export default useCandidatesPicker;
